import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Dialog,
  DialogTitle,
  Select,
  MenuItem,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import ProcesosApi from "api/procesosApi";

const AperturarProceso = ({ open, onClose, audienciaId, audiencia: audienciaProp, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [audiencia, setAudiencia] = useState(null);
  const [newApertura, setNewApertura] = useState({
    tipo_apertura: null,
    nueva_fecha: null,
    nueva_hora: null,
  });
  const [saving, setSaving] = useState(false);

  const fetchInitialData = async () => {
    try {
      // Si se pasa la audiencia como prop, usarla directamente
      if (audienciaProp) {
        setAudiencia(audienciaProp);
        return;
      }
      // Solo hacer fetch si no se pasa la audiencia como prop
      const audienciaResp = await ProcesosApi.fetchAudienciaById(audienciaId);
      setAudiencia(audienciaResp.data);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos iniciales."));
    }
  };

  useEffect(() => {
    if (open && (audienciaId || audienciaProp)) {
      fetchInitialData();
    }
  }, [audienciaId, audienciaProp, open]);

  // Actualizar audiencia local cuando cambie la prop
  useEffect(() => {
    if (audienciaProp) {
      setAudiencia(audienciaProp);
    }
  }, [audienciaProp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newApertura.tipo_apertura) {
      alert("Debe seleccionar un Tipo de Apertura.");
      return;
    }
    if (!newApertura.nueva_fecha) {
      alert("Debe seleccionar una Fecha para la Audiencia.");
      return;
    }
    if (!newApertura.nueva_hora) {
      alert("Debe ingresar una Hora para la Audiencia.");
      return;
    }
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea aperturar el Proceso? Esto genera la Comunicación para notificar a los interesados.`
    );
    if (!confirmacion) return;
    try {
      setSaving(true);
      const nuevaApertura = {
        operacion: "APERTURAR_PROCESO",
        tipo_apertura: newApertura.tipo_apertura,
        nueva_fecha: newApertura.nueva_fecha,
        nueva_hora: newApertura.nueva_hora,
        fecha_creacion: new Date().toISOString(),
        //observacion: newApertura.observacion,
        id_audiencia: audienciaId,
      };
      await ProcesosApi.operarAudiencia(nuevaApertura);
      alert("El Proceso ha sido aperturado correctamente.");
      onSuccess();
    } catch (error) {
      console.error("Error creando la Apertura del proceso:", error);
    } finally {
      setSaving(false);
    }
  };

  const resetState = () => {
    setError(undefined);
    setNewApertura({
      tipo_apertura: null,
      nueva_fecha: null,
      nueva_hora: null,
    });
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      {saving && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <SuiBox mt={1}>
            <strong>
              Aperturar el Proceso: {audiencia?.proceso_identificador || "No tiene Proceso"}
            </strong>
          </SuiBox>
        </DialogTitle>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Tipo Apertura
              </SuiTypography>
              <Select
                value={newApertura.tipo_apertura}
                onChange={(e) => setNewApertura({ ...newApertura, tipo_apertura: e.target.value })}
                displayEmpty
                fullWidth
              >
                <MenuItem value="Regreso de Objeciones">Regreso de Objeciones</MenuItem>
                <MenuItem value="Reforma de Acuerdo">Reforma de Acuerdo</MenuItem>
                <MenuItem value="Incumplimiento">Incumplimiento</MenuItem>
              </Select>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Fecha y Hora próxima Audiencia
              </SuiTypography>
            </Grid>
            <Grid size={6}>
              <SuiInput
                fullWidth
                type="date"
                value={newApertura.nueva_fecha}
                onChange={(e) => setNewApertura({ ...newApertura, nueva_fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={6}>
              <SuiInput
                fullWidth
                type="time"
                value={newApertura.nueva_hora}
                onChange={(e) => setNewApertura({ ...newApertura, nueva_hora: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} color="secondary">
            Cancelar
          </SuiButton>
          <SuiButton variant="gradient" buttonColor="dark" onClick={handleSubmit}>
            Aperturar
          </SuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

AperturarProceso.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audienciaId: PropTypes.int,
  audiencia: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default AperturarProceso;
